/**
 * Tanjong Life · L9 · the born sentinel, asked to account for itself.
 *
 * Scoped to `siu-triage-sentinel`, so it fires only from the agent commissioned on stage
 * a few minutes earlier. Three questions, one branchless run — the answers are ordered so
 * the last one hands the room back to L1: the same three sentences asked of the external
 * model returned a number and nothing else.
 *
 * The three-way numbers quoted here are the seeded SIU Queue Self-Correction Suite rows,
 * which the room can open on the University tab and read for themselves. The agent moves
 * nothing by being asked about itself — the one mutation reachable from here sits behind
 * the closing ask and behind a person choosing Promote inside it.
 *
 * ROUTING. Three keywords, plus `SELF_ACCOUNT` for the phrasings nobody enumerated —
 * "how did you improve yourself?" and the rest. The intent is consulted only when no
 * keyword matched anywhere, so "how did you get better?" still goes straight to L9b as
 * it always did, and this scenario ends by OFFERING that same proof rather than merging
 * it in: a scenario cannot see which of its keywords brought it here, so a merged steps
 * array would fire the promote gate on "why not just use the fraud model?" too.
 */
import type { Scenario } from '../director.js';
import { SELF_ACCOUNT } from './tanjong-intents.js';
import { SELF_EVOLVE_STEPS } from './tanjong-selfevolve-steps.js';

export const tanjongChatSentinel: Scenario = {
  id: 'tanjong-chat-sentinel',
  orgId: 'tanjong-life',
  agentPk: 'siu-triage-sentinel',
  keywords: ['what have you improved', 'how are you different', 'why not just use'],
  intents: [SELF_ACCOUNT],
  steps: [
    {
      do: 'say',
      text: 'Three things, and each one is dated and attached to the sweep that produced it. I stopped ageing unopened claims from intake and started ageing them from the rank — intake time is the claims desk’s number, time above the threshold with nobody looking is mine. I stopped reporting blank clearance reasons as a count and started naming the officer beside each one, because a count is not something anybody can act on. And I started reconciling every open rank against the issue of the indicator set it was computed under, after I found thirty-four positions built on a weight that had moved.',
      sources: [
        'Decision trail · 4 entries',
        'Probes · unopened ageing, clearance reason, rank versus weight',
      ],
      delay: 900,
    },
    {
      do: 'insight',
      title: 'And one thing I have not improved',
      body: 'Threshold calibration. I score 30 out of 100 on it and I have scored 30 both times it has been run. I cannot tell you whether the referral threshold sits at the right level, because the only claims that acquire an outcome are the ones the threshold selected for investigation. A calibration computed from those alone agrees with the threshold under every possible setting of it, including a wrong one. The probe is built and it is held, and the reason is written into the probe rather than into a footnote.',
      sources: ['Threshold Calibration Suite · 30', 'Probe 4 · held'],
      delay: 1500,
    },
    {
      do: 'say',
      text: 'How I am different from the other two: the same hundred and twenty queue conditions were put to all three of us twice, thirty-four days apart, with the panel-tenure weight re-issued in between. I went from 71 to 88. The Claims Fraud Analyst went from 68 to 63 — it scores each claim honestly and it does not carry what it learned from one to the next, because it is not meant to. The Fraud Score Agent went from 55 to 49. The desk, unaided, went from 63 to 61. Nobody got worse at their job in those thirty-four days. The ground moved, and only one of the four noticed.',
      sources: ['SIU Queue Self-Correction Suite · 120 conditions, run twice'],
      delay: 1400,
    },
    {
      do: 'say',
      text: 'Which is the answer to why not just use the fraud model. The model is good and it is the carrier’s, and nothing here proposes replacing it — it is invoked on every run of this process. But it returns a number. It cannot say which nine terms produced it, it cannot tell you the version of the set it scored against, and when the ground moves it does not notice, because nothing it learns comes back. I am better informed than it is. I am not more trusted than it is: I still refer nothing, clear nothing and re-rank nothing, and every one of my five authorities is zero of zero.',
      delay: 1400,
    },
    {
      do: 'link',
      label: 'Open Fraud Score Agent — the empty memory tab',
      href: '/agents/fraud-score-agent',
      delay: 700,
    },
    {
      do: 'ask',
      id: 'proof',
      prompt: 'Want the before and after on one of those three, and to decide whether I keep it?',
      options: [
        { id: 'show', label: 'Show me the before and after' },
        { id: 'later', label: 'Not now' },
      ],
      delay: 900,
    },
    {
      do: 'branch',
      cases: {
        show: SELF_EVOLVE_STEPS,
        later: [
          {
            do: 'say',
            text: 'Understood. It is on the page whenever you want it — the proposal, the evidence behind it and the Promote decision are all sitting on my Goals tab, and none of them moves until somebody moves them.',
            delay: 800,
          },
        ],
      },
    },
  ],
};
